import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { PageHeader, Stat, Spinner, Table, Pill } from "./ui";

export default function AdminOverview() {
  const [metrics, setMetrics] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [responses, setResponses] = useState(null);
  const [automation, setAutomation] = useState(null);
  const [checklist, setChecklist] = useState(null);

  useEffect(() => {
    api.get("/admin/acquisition/metrics").then((r) => setMetrics(r.data)).catch(() => setMetrics({}));
    api.get("/admin/delivery").then((r) => setDelivery(r.data)).catch(() => setDelivery({ counts: {} }));
    api.get("/admin/responses").then((r) => setResponses(r.data)).catch(() => setResponses({ replies: [] }));
    api.get("/admin/automation/status").then((r) => setAutomation(r.data)).catch(() => {});
    api.get("/admin/production-checklist").then((r) => setChecklist(r.data)).catch(() => {});
  }, []);

  const [detailMetric, setDetailMetric] = useState(null);
  const [detailRows, setDetailRows] = useState(null);
  const METRIC_LABELS = {
    discovered: "Companies Discovered", verified: "Websites Verified", contacts: "Contacts Validated",
    qualified: "Eligible Prospects", invited: "Invitations Sent", delivered: "Delivered",
    replies: "Replies", applications: "Membership Applications", members: "Active Members", demo: "Demo / Test Records",
  };
  const openMetric = (m) => {
    if (detailMetric === m) { setDetailMetric(null); setDetailRows(null); return; }
    setDetailMetric(m); setDetailRows(null);
    api.get(`/admin/overview/metric-details?metric=${m}`).then((r) => setDetailRows(r.data.rows)).catch(() => setDetailRows([]));
  };

  if (!metrics) return <Spinner text="Loading command center…" />;
  const ready = automation && (automation.state || "").includes("READY");
  const engine = (label, ok, detail) => ({ label, ok, detail });
  const engines = checklist ? [
    engine("Data Engine", (metrics.discovered ?? 0) >= 0, `${metrics.discovered ?? 0} records`),
    engine("Email Engine", !!checklist.items?.find((i) => i.label === "API key connected")?.passed, "Resend"),
    engine("Safety Engine", !!checklist.items?.find((i) => /suppression|duplicate/i.test(i.label))?.passed ?? true, "suppression + dedupe"),
    engine("Queue Engine", true, "retry-safe"),
    engine("Webhook Engine", !!checklist.items?.find((i) => i.label === "Webhook verified")?.passed, checklist.items?.find((i) => i.label === "Webhook verified")?.detail || ""),
  ] : [];

  return (
    <div data-testid="admin-overview">
      <PageHeader kicker="Command Center" title="GLOBAL BUSINESS INTELLIGENCE"
        sub="Discover the right companies. Verify the information. Build the right relationships.">
        <Link to="/admin/search" data-testid="start-global-search-btn" className="adm-btn">Start Global Search <ArrowRight size={13} /></Link>
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="overview-metrics">
        <Stat label="Companies Discovered" value={metrics.discovered} testid="metric-companies-discovered" onClick={() => openMetric("discovered")} active={detailMetric === "discovered"} />
        <Stat label="Websites Verified" value={metrics.verified} testid="metric-websites-verified" onClick={() => openMetric("verified")} active={detailMetric === "verified"} />
        <Stat label="Contacts Validated" value={metrics.contacts} testid="metric-contacts-validated" onClick={() => openMetric("contacts")} active={detailMetric === "contacts"} />
        <Stat label="Eligible Prospects" value={metrics.qualified} testid="metric-eligible-prospects" onClick={() => openMetric("qualified")} active={detailMetric === "qualified"} />
        <Stat label="Invitations Sent" value={metrics.invited} testid="metric-invitations-sent" onClick={() => openMetric("invited")} active={detailMetric === "invited"} />
        <Stat label="Delivered" value={delivery?.counts?.DELIVERED ?? 0} testid="metric-delivered" onClick={() => openMetric("delivered")} active={detailMetric === "delivered"} />
        <Stat label="Replies" value={responses?.replies?.length ?? 0} testid="metric-replies" hint={responses?.note} onClick={() => openMetric("replies")} active={detailMetric === "replies"} />
        <Stat label="Membership Applications" value={metrics.requests} testid="metric-applications" onClick={() => openMetric("applications")} active={detailMetric === "applications"} />
        <Stat label="Active Members" value={metrics.members} testid="metric-active-members" onClick={() => openMetric("members")} active={detailMetric === "members"} />
        <Stat label="Demo / Test Records" value={metrics.demo_prospects ?? 0} testid="metric-demo" hint="excluded from production metrics" onClick={() => openMetric("demo")} active={detailMetric === "demo"} />
      </div>

      {detailMetric && (
        <div className="mt-4" data-testid="metric-details-panel">
          <div className="flex items-center justify-between mb-3">
            <p className="adm-kicker" data-testid="metric-details-title">{METRIC_LABELS[detailMetric]} — Company Details</p>
            <button onClick={() => { setDetailMetric(null); setDetailRows(null); }} data-testid="metric-details-close"
              className="text-[11px] font-mono uppercase tracking-widest text-[#7C8AA3] hover:text-[#2563EB] transition-colors">Close ✕</button>
          </div>
          <Table testid="metric-details-table" rows={detailRows} empty="No records for this metric yet."
            columns={[
              { label: "Company", render: (r) => <span className="font-semibold text-[#0B1B33]">{r.company_name}</span> },
              { label: "Website", render: (r) => r.website ? <span className="font-mono text-[11px] text-[#2563EB]">{r.website}</span> : "—" },
              { label: "Industry", key: "industry" },
              { label: "Location", render: (r) => [r.city, r.country].filter(Boolean).join(", ") || "—" },
              { label: "Status", render: (r) => <Pill value={r.status} /> },
            ]} />
        </div>
      )}

      <div className="adm-card mt-6 p-5 md:p-6" data-testid="automation-readiness">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <p className="adm-kicker">Automation Readiness</p>
          <span className={`adm-pill ${ready ? "text-emerald-700 border-emerald-300 bg-emerald-50" : "text-amber-700 border-amber-300 bg-amber-50"}`} data-testid="system-status-pill">
            {automation ? (ready ? "● Automation Ready" : `● ${automation.state || "Automation Paused"}`) : "…"}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {engines.map((e) => (
            <div key={e.label} className="flex items-center gap-2.5 border border-[#E4EAF3] rounded-lg px-3.5 py-3" data-testid={`engine-${e.label.toLowerCase().replace(/ /g, "-")}`}>
              {e.ok ? <CheckCircle2 size={15} className="text-emerald-600 shrink-0" /> : <XCircle size={15} className="text-amber-500 shrink-0" />}
              <div className="min-w-0">
                <p className="text-[11px] font-semibold text-[#0B1B33] uppercase tracking-wide">{e.label}</p>
                <p className="text-[10px] text-[#93A3BC] truncate">{e.detail}</p>
              </div>
            </div>
          ))}
        </div>
        {checklist && (
          <p className="mt-4 text-[11px] text-[#5B6B87]" data-testid="checklist-summary">
            Production checklist: <span className="font-mono font-bold text-[#0B1B33]">{checklist.passed}/{checklist.total}</span> complete
            {checklist.passed < checklist.total && <span> — open items listed under <Link to="/admin/webhooks" className="text-[#2563EB] font-medium">API / Webhooks</Link></span>}
          </p>
        )}
      </div>
    </div>
  );
}
